import {
  Sstv2DlqMessage,
  Sstv2MigrationAttemptState,
} from "src/features/partners/partnersApi"
import { Sstv2MigrationRow, isRollbackPhase } from "./sstv2MigrationTypes"
import { describeFlipVerdict } from "./sstv2Vocabulary"

/**
 * Pure interpretation of one attempt's live state (DEV-193, admin-UI pass).
 *
 * The backend read path is deliberately phase-agnostic — it reports which of two
 * native ids resolves and nothing more. All of the "what does this MEAN" lives
 * here, in pure functions, for two reasons: the same three buckets mean completely
 * different things before and after a rollback, and the conclusions are exactly the
 * sort of thing that must be unit-testable rather than eyeballed in a drawer.
 */

// ---------------------------------------------------------------------------
// Flip liveness
// ---------------------------------------------------------------------------

export type FlipVerdict =
  /** The flip function is not wired at all — nothing will ever flip. */
  | "not-configured"
  /** An execution is alive right now. */
  | "running"
  /** A ROLLBACK execution is alive — read every other number with that in mind. */
  | "rolling-back"
  /**
   * Cut over, mappings still unflipped, and NO live execution. This is verbatim
   * the reconciler's stalled predicate; the 15-minute tick restarts it.
   */
  | "stalled"
  /** Cut over and everything the sample covered is flipped. */
  | "done"
  /** Not cut over yet — there is no flip to have an opinion about. */
  | "not-started"
  /** Rolled back and no rows still answer to V2 — the unwind finished. */
  | "rolled-back"

/**
 * True once the attempt has reached cutover, which is the ONLY point at which a
 * flip may exist. Mirrors the backend detector: phase at/after `OPERATIONAL`, or
 * the operational event having been seen at all (the two can disagree, because
 * `COMPLETED` and `OPERATIONAL` arrive in either order).
 */
export const hasCutOver = (row: Sstv2MigrationRow): boolean =>
  row.phase === "OPERATIONAL" ||
  row.phase === "COMPLETED" ||
  !!row.operationalEventReceived

/**
 * Classify the flip. Order matters and is cheapest-signal-first, exactly like the
 * reconciler: a not-configured flip makes every other verdict meaningless, a live
 * rollback explains a falling `liveOnV2`, and "stalled" is only reachable once
 * both of those are ruled out.
 */
export const flipVerdict = (
  row: Sstv2MigrationRow,
  state?: Sstv2MigrationAttemptState,
): FlipVerdict => {
  if (!state) return "not-started"
  if (!state.flip.configured) return "not-configured"
  if (state.flip.rollbackRunning) return "rolling-back"
  if (state.flip.flipRunning) return "running"

  if (isRollbackPhase(row.phase)) {
    // After a rollback the meaningful question inverts: anything still on V2 is
    // an INCOMPLETE unwind, not progress.
    return state.active.liveOnV2 > 0 ? "stalled" : "rolled-back"
  }

  if (!hasCutOver(row)) return "not-started"
  // No mappings recorded at all is not "done" — under D15 it more likely means the
  // ingest never landed. `total` (a COUNT) is the honest input here, not the sample.
  if (state.active.total === 0) return "not-started"
  return state.active.liveOnV1 > 0 || state.active.absent > 0
    ? "stalled"
    : "done"
}

/**
 * i18n keys per verdict, written out rather than built as `migrationFlip_${v}`.
 *
 * Two reasons this is not pedantry: a constructed key is invisible to a grep for
 * unused/missing locale entries, and in this app an EXISTING key silently overrides
 * a `t()` defaultValue — so a typo in a template produces a plausible-looking
 * fallback instead of an error. Exhaustive maps make a missing key a type error.
 *
 * Both properties now live in `./sstv2Vocabulary`, which holds the same written-out
 * mapping alongside the plain-English gloss for each verdict, and which resolves
 * TOLERANTLY: a verdict this build does not model returns "unknown" plus its raw
 * value instead of `undefined`. That matters because `t(undefined)` renders as
 * nothing at all — a blank chip where a severe warning belongs.
 */
export const flipVerdictLabelKey = (verdict: FlipVerdict): string =>
  describeFlipVerdict(verdict).labelKey

export const flipVerdictHintKey = (verdict: FlipVerdict): string =>
  describeFlipVerdict(verdict).glossKey

/** Severity for the flip callout. `severe` = an operator is affected right now. */
export const flipSeverity = (
  verdict: FlipVerdict,
): "severe" | "advisory" | "info" | "none" => {
  switch (verdict) {
    // Not-configured and stalled both mean occupied doors that the dashboard
    // cannot open. Stalled self-heals within 15 minutes, but "self-healing" is
    // not the same as "fine", and the empty-materialization incident is exactly
    // what a quiet field produced last time.
    case "not-configured":
    case "stalled":
      return "severe"
    case "rolling-back":
      return "advisory"
    case "running":
      return "info"
    default:
      return "none"
  }
}

// ---------------------------------------------------------------------------
// Rollback outcome — restored is NOT deleted
// ---------------------------------------------------------------------------

export type RollbackOutcome = {
  /** Reverted to V1 identity and STILL LIVE. Nothing was removed. */
  restored: number
  /** Adopted rows, archived out of the live table — the only real removals. */
  archived: number
  /** Rows that still answer to V2: the unwind did not finish. */
  stillOnV2: number
  /** True when `stillOnV2 > 0` — loud, because the attempt is half-unwound. */
  incomplete: boolean
  /** True when the buckets describe a sample rather than the whole set. */
  truncated: boolean
}

/**
 * Reconstruct the rollback outcome. Returns `null` for a non-rolled-back attempt,
 * because these words mean nothing there.
 *
 * The reason this is worth a named type: `reservationsRestored` and
 * `reservationsDeleted` are separate fields on `RollbackCleanupResult` precisely
 * because a restored row never left the live table. Adding them together — or
 * labelling restores as deletions — reports a textbook-correct rollback as data
 * loss, which is the single most alarming thing this page could get wrong.
 */
export const rollbackOutcome = (
  row: Sstv2MigrationRow,
  state?: Sstv2MigrationAttemptState,
): RollbackOutcome | null => {
  if (!state || !isRollbackPhase(row.phase)) return null
  return {
    restored: state.active.liveOnV1,
    archived: state.active.absent,
    stillOnV2: state.active.liveOnV2,
    incomplete: state.active.liveOnV2 > 0,
    truncated: state.active.truncated,
  }
}

// ---------------------------------------------------------------------------
// Ingestion DLQ
// ---------------------------------------------------------------------------

export type IngestionDlqState = {
  /** Approximate depth of the ingestion DLQ. `null` when it could not be read. */
  depth: number | null
  /** Peeked messages the tick has given up re-driving. */
  exhausted: number
  /** Peeked messages still under the cap — the tick will retry these itself. */
  retrying: number
  /** True when the peek was capped, so `exhausted` is a lower bound. */
  peekTruncated: boolean
}

/**
 * The peek returns at most 10 messages, so `exhausted` is a LOWER BOUND whenever
 * the depth exceeds what was peeked. Surfaced rather than smoothed over: "0
 * exhausted" computed from 10 of 4,000 messages is a lie that reads as reassurance.
 */
export const PEEK_MAX = 10

export const ingestionDlqState = (
  depth: number | null,
  messages: Sstv2DlqMessage[],
): IngestionDlqState => {
  const exhausted = messages.filter((m) => m.redriveExhausted).length
  return {
    depth,
    exhausted,
    retrying: messages.length - exhausted,
    peekTruncated: (depth ?? 0) > messages.length,
  }
}

/**
 * Which SQS config row is the INGESTION queue.
 *
 * Matched on the queue NAME rather than the config tag: the tag is partner-side
 * configuration an operator can rename, while the queue name comes from our own
 * CloudFormation resource. Since the second queue was deleted this is expected to
 * match exactly one row, but the match stays a filter so an extra config cannot
 * make the panel silently pick the wrong queue.
 */
export const INGESTION_QUEUE_NAME_FRAGMENT = "sstv2-sqs-queue"
